// Safe, document.write-free loader for the UMD/global build.
//
// Chrome warns about (and may block) a parser-blocking, cross-site script that is
// injected via document.write. This helper instead appends a normal ASYNC script
// tag and resolves a Promise once window.Butterfly.createSDK is available — so a
// plain HTML page can bootstrap the SDK without document.write.
//
// (Module/bundler users don't need this — just
//   import { createSDK } from 'https://butterfly-sdk.pages.dev/butterfly-sdk.js')

const DEFAULT_UMD_URL = 'https://butterfly-sdk.pages.dev/butterfly-sdk.umd.js';
const DEFAULT_TIMEOUT_MS = 15000;
const SCRIPT_MARK = 'data-butterfly-sdk';

let _pending = null; // shared in-flight load so concurrent callers reuse one <script>

export function loadButterflySDK(url = DEFAULT_UMD_URL, { timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  // Already loaded → reuse it.
  if (typeof window !== 'undefined' && window.Butterfly?.createSDK) {
    return Promise.resolve(window.Butterfly);
  }
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    return Promise.reject(new Error('[ButterflySDK] loadButterflySDK requires a browser DOM.'));
  }
  if (_pending) return _pending;

  _pending = new Promise((resolve, reject) => {
    let timer;
    const settle = (fn, arg) => { clearTimeout(timer); _pending = null; fn(arg); };
    const succeed = () => {
      if (window.Butterfly?.createSDK) settle(resolve, window.Butterfly);
      else settle(reject, new Error('[ButterflySDK] script loaded but window.Butterfly.createSDK is missing.'));
    };
    const fail = () => settle(reject, new Error(`[ButterflySDK] failed to load ${url}`));

    timer = setTimeout(() => settle(reject, new Error('[ButterflySDK] loadButterflySDK timed out.')), timeoutMs);

    // Reuse a script tag the page may already have added (e.g. <script defer>).
    let script = document.querySelector(`script[${SCRIPT_MARK}]`)
      || document.querySelector(`script[src="${url}"]`);

    if (!script) {
      script = document.createElement('script');
      script.src = url;
      script.async = true;                 // never parser-blocking; never document.write
      script.setAttribute(SCRIPT_MARK, '');
      (document.head || document.documentElement).appendChild(script);
    }

    script.addEventListener('load', succeed, { once: true });
    script.addEventListener('error', fail, { once: true });

    // If a matching script already finished loading before we attached listeners.
    if (window.Butterfly?.createSDK) succeed();
  });

  return _pending;
}
